import React, { useState, useEffect } from 'react'
import { Container } from '../../components/Container'
import { CircularProgress } from '@material-ui/core'
import Plot from 'react-plotly.js'
import { KPIController } from '../../hooks/useKPI'

export const MoveInInspections = ({
    KPIController,
}: {
    KPIController: KPIController
}) => {
    const [plotWidth, setPlotWidth] = useState(0)
    const [plotHeight, setPlotHeight] = useState(0)

    useEffect(() => {
        KPIController.getMoveInInspections()
    }, [])

    // set the plot size
    useEffect(() => {
        const updateDimensions = () => {
            // Get the container element and calculate dimensions
            const container = document.querySelector('.plot-container')
            if (container) {
                // Subtracting 20px from each dimension for padding
                setPlotWidth(container.clientWidth - 20)
                setPlotHeight(container.clientHeight - 20)
            }
        }
        // Initial calculation
        updateDimensions()

        // Add resize listener
        window.addEventListener('resize', updateDimensions)

        // Cleanup
        return () => window.removeEventListener('resize', updateDimensions)
    }, [])

    const calculateMonthlyGrowth = (monthData: {
        x: string[]
        y: number[]
    }) => {
        const growthText = new Array(monthData.x.length).fill('')

        monthData.y.forEach((value, index) => {
            // Skip the first month since we can't calculate growth for it
            if (index === 0) return

            const currentValue = value
            const prevValue = monthData.y[index - 1]

            // Calculate month-over-month growth percentage
            if (prevValue !== 0) {
                const growth = ((currentValue - prevValue) / prevValue) * 100
                growthText[index] = `${growth.toFixed(1)}%`
            }
        })

        return growthText
    }

    // Helper to get year ranges for shading
    const getYearRanges = (
        xVals: string[],
    ): Array<{ year: string; startIdx: number; endIdx: number }> => {
        const ranges: Array<{
            year: string
            startIdx: number
            endIdx: number
        }> = []
        let currentYear = ''
        let startIdx = 0
        xVals.forEach((x, i) => {
            const year = x.slice(0, 4)
            if (year !== currentYear) {
                if (currentYear !== '') {
                    ranges.push({ year: currentYear, startIdx, endIdx: i - 1 })
                }
                currentYear = year
                startIdx = i
            }
        })
        if (currentYear !== '') {
            ranges.push({
                year: currentYear,
                startIdx,
                endIdx: xVals.length - 1,
            })
        }
        return ranges
    }

    // Helper to calculate percentage values for complete
    const calculatePercentages = (
        completeData: { x: string[]; y: number[] },
        autoData: { x: string[]; y: number[] },
    ): number[] => {
        const length = Math.max(completeData.y.length, autoData.y.length)
        const completePercents: number[] = []
        for (let i = 0; i < length; i++) {
            const complete = completeData.y[i] ?? 0
            const auto = autoData.y[i] ?? 0
            const total = complete + auto
            if (total === 0) {
                completePercents.push(0)
            } else {
                completePercents.push((complete / total) * 100)
            }
        }
        return completePercents
    }

    // Helper to calculate linear regression (trendline)
    const calculateTrendline = (yVals: number[]): number[] => {
        const n = yVals.length
        const xVals = Array.from({ length: n }, (_, i) => i)
        const sumX = xVals.reduce((a, b) => a + b, 0)
        const sumY = yVals.reduce((a, b) => a + b, 0)
        const sumXY = xVals.reduce((sum, x, i) => sum + x * yVals[i], 0)
        const sumXX = xVals.reduce((sum, x) => sum + x * x, 0)
        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
        const intercept = (sumY - slope * sumX) / n
        return xVals.map((x) => slope * x + intercept)
    }

    // Helper to format date strings as 'MMM YYYY'
    const formatMonthYear = (dateStr: string): string => {
        const date = new Date(dateStr)
        return date.toLocaleString('default', {
            month: 'short',
            year: 'numeric',
        })
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                height: 'calc(100vh - 200px)',
            }}
            className="plot-container"
        >
            {/* plot */}
            <Container
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {KPIController.moveInInspections ? (
                    (() => {
                        const {
                            complete,
                            auto,
                        } = KPIController.moveInInspections
                        const completePercents = calculatePercentages(
                            complete,
                            auto,
                        )
                        const xVals = complete.x
                        const yearRanges = getYearRanges(xVals)
                        const yearColors = ['rgba(207, 123, 19, 0.25)']
                        const shapes = yearRanges.map((range, idx) => ({
                            type: 'rect' as const,
                            xref: 'x' as const,
                            yref: 'paper' as const,
                            x0: xVals[range.startIdx],
                            x1: xVals[range.endIdx],
                            y0: 0,
                            y1: 1,
                            fillcolor: yearColors[idx % yearColors.length],
                            opacity: 1,
                            layer: 'below' as const,
                            line: { width: 0 },
                        }))
                        const trendline = calculateTrendline(completePercents)
                        const formattedXVals = xVals.map(formatMonthYear)
                        return (
                            <Plot
                                data={[
                                    {
                                        x: formattedXVals,
                                        y: completePercents,
                                        text: complete.y.map(
                                            (count, i) => `${count}`,
                                        ),
                                        mode: 'lines+markers' as const,
                                        marker: { size: 5 },
                                        line: { shape: 'linear' },
                                        type: 'scatter' as const,
                                        name: 'Complete',
                                        hovertemplate:
                                            'Month: %{x}<br>Count: %{text}<br>Percent: %{y:.1f}%<extra></extra>',
                                    },
                                    {
                                        x: formattedXVals,
                                        y: trendline,
                                        mode: 'lines' as const,
                                        line: {
                                            dash: 'dot',
                                            color: 'black',
                                            width: 2,
                                        },
                                        type: 'scatter' as const,
                                        name: 'Trendline',
                                        hoverinfo: 'skip',
                                        showlegend: true,
                                    },
                                ]}
                                layout={{
                                    width: plotWidth,
                                    height: plotHeight,
                                    title: {
                                        text:
                                            'Move In Inspections Completed V. Auto',
                                    },
                                    margin: { t: 50, b: 50, l: 50, r: 50 },
                                    yaxis: {
                                        range: [0, 100],
                                        title: 'Percentage (%)',
                                    },
                                    shapes: shapes.map((shape, i) => ({
                                        ...shape,
                                        x0:
                                            formattedXVals[
                                                yearRanges[i].startIdx
                                            ],
                                        x1:
                                            formattedXVals[
                                                yearRanges[i].endIdx
                                            ],
                                    })),
                                }}
                                useResizeHandler={true}
                                style={{ width: '100%', height: '100%' }}
                            />
                        )
                    })()
                ) : (
                    <Container
                        style={{
                            flex: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Container>
                )}
            </Container>
        </Container>
    )
}
